/** Add data: a spreadsheet becomes a topic in three steps.
 *
 *   1 Source    a file dropped here, or a Google Sheet link read once, now
 *   2 Check     the sheet as the server read it, every decision with an undo
 *   3 Describe  a name and plain words for the assistant, drafted and edited
 *
 * There is no template and nothing to map: the columns keep their names. The
 * server does the reading (backend/chainlit/data_upload.py); this is the form.
 * With `replace` set, the same steps bring a new version of an existing topic.
 */
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  FileSpreadsheet,
  Link2,
  Loader2,
  RotateCcw,
  Sparkles,
  Upload
} from 'lucide-react';
import { useCallback, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';

import {
  Draft,
  Notice,
  Preview,
  PreviewColumn,
  Staged,
  UploadOptions,
  UploadTopic,
  applyUndo,
  commit,
  describe,
  formatCount,
  linkSheet,
  parseFile,
  reparse
} from './api';

type Step = 1 | 2 | 3;

const ACCEPT = '.xlsx,.xlsm,.csv,.tsv,.txt';

function Steps({ step }: { step: Step }) {
  const items: [Step, string][] = [
    [1, 'Source'],
    [2, 'Check'],
    [3, 'Describe']
  ];
  return (
    <ol aria-label="Steps" className="flex flex-wrap items-center gap-2">
      {items.map(([n, label], i) => {
        const done = n < step;
        const now = n === step;
        return (
          <li key={n} className="flex items-center gap-2">
            {i > 0 && (
              <span aria-hidden="true" className="mx-1 h-px w-8 bg-border" />
            )}
            <span
              aria-hidden="true"
              className={
                'inline-flex size-6 items-center justify-center rounded-full text-xs font-semibold ' +
                (done
                  ? 'bg-emerald-600 text-white'
                  : now
                    ? 'bg-primary text-primary-foreground'
                    : 'border border-border text-muted-foreground')
              }
            >
              {done ? <Check className="size-3.5" /> : n}
            </span>
            <span
              className={
                'text-sm ' +
                (now ? 'font-semibold' : 'font-medium text-muted-foreground')
              }
            >
              {label}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

function TypeChip({ column }: { column: PreviewColumn }) {
  const tone =
    column.type === 'number'
      ? 'bg-emerald-50 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200'
      : column.type === 'date'
        ? 'bg-blue-50 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200'
        : 'bg-muted text-muted-foreground';
  const extra =
    column.type !== 'text' && column.min != null && column.max != null
      ? ` · ${String(column.min)} to ${String(column.max)}`
      : column.empty_pct >= 20
        ? ` · ${column.empty_pct}% empty`
        : '';
  return (
    <span
      className={`inline-block w-fit rounded-full px-2 py-0.5 text-[11px] font-medium ${tone}`}
    >
      {column.type}
      {extra}
    </span>
  );
}

function cell(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'number') return v.toLocaleString('en-US');
  return String(v);
}

function NoticeCard({
  notice,
  onUndo,
  busy
}: {
  notice: Notice;
  onUndo: (undo: UploadOptions) => void;
  busy: boolean;
}) {
  const tag =
    notice.kind === 'left_out'
      ? [
          'Left out',
          'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200'
        ]
      : notice.kind === 'changed'
        ? [
            'Changed',
            'bg-blue-100 text-blue-900 dark:bg-blue-900/40 dark:text-blue-200'
          ]
        : ['Kept', 'bg-muted text-muted-foreground'];
  return (
    <div className="flex flex-col gap-2 rounded-xl border border-border p-3.5">
      <div className="flex items-start gap-2">
        <span
          className={`mt-0.5 shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${tag[1]}`}
        >
          {tag[0]}
        </span>
        <span className="text-sm font-semibold leading-5">{notice.title}</span>
      </div>
      <p className="text-sm leading-5 text-muted-foreground">{notice.text}</p>
      {notice.undo && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-fit"
          disabled={busy}
          onClick={() => onUndo(notice.undo!)}
        >
          {notice.undo_label ?? 'Undo'}
        </Button>
      )}
    </div>
  );
}

export default function AddDataWizard({
  replace,
  onDone,
  onCancel
}: {
  /** The topic a new version is for; null when adding a new one. */
  replace: UploadTopic | null;
  onDone: (topic: UploadTopic) => void;
  onCancel: () => void;
}) {
  const [step, setStep] = useState<Step>(1);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [staged, setStaged] = useState<Staged | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [options, setOptions] = useState<UploadOptions>({});
  const [sheetUrl, setSheetUrl] = useState('');
  const [headerRow, setHeaderRow] = useState('');
  const [dragging, setDragging] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  // Describe
  const [draft, setDraft] = useState<Draft | null>(null);
  const [title, setTitle] = useState(replace?.title ?? '');
  const [description, setDescription] = useState('');
  const [grain, setGrain] = useState(replace?.grain_text ?? '');
  const [dateColumn, setDateColumn] = useState<string>(
    replace?.date_column ?? ''
  );
  const [examples, setExamples] = useState<string[]>([]);

  const fail = (e: unknown) =>
    setError(e instanceof Error ? e.message : 'Something went wrong.');

  const begin = useCallback((s: Staged) => {
    setStaged(s);
    setPreview(s.preview);
    setOptions(s.preview.options ?? {});
    setHeaderRow(String(s.preview.header_row));
    setStep(2);
  }, []);

  const onFile = async (file: File | null | undefined) => {
    if (!file) return;
    setError(null);
    setBusy('Reading the file…');
    try {
      begin(await parseFile(file));
    } catch (e) {
      fail(e);
    } finally {
      setBusy(null);
    }
  };

  const onLink = async () => {
    if (!sheetUrl.trim()) return;
    setError(null);
    setBusy('Reading the sheet…');
    try {
      begin(await linkSheet(sheetUrl.trim()));
    } catch (e) {
      fail(e);
    } finally {
      setBusy(null);
    }
  };

  const apply = async (next: UploadOptions) => {
    if (!staged) return;
    setError(null);
    setBusy('Reading again…');
    try {
      const r = await reparse(staged.token, next);
      setOptions(next);
      setPreview(r.preview);
      setHeaderRow(String(r.preview.header_row));
    } catch (e) {
      fail(e);
    } finally {
      setBusy(null);
    }
  };

  const draftNow = useCallback(
    async (wanted: string | null) => {
      if (!staged) return;
      setBusy('Drafting…');
      setError(null);
      try {
        const d = await describe(staged.token, options, wanted);
        setDraft(d);
        if (!replace) {
          setTitle((t) => t || d.title);
          setGrain(d.grain || '');
          setDateColumn(d.date_column ?? '');
        }
        setDescription(d.description);
        setExamples(d.examples ?? []);
      } catch (e) {
        fail(e);
      } finally {
        setBusy(null);
      }
    },
    [staged, options, replace]
  );

  const save = async () => {
    if (!staged || !preview) return;
    if (!title.trim() || !description.trim()) {
      setError('Give it a name and a few words about what it is.');
      return;
    }
    setBusy('Saving…');
    setError(null);
    try {
      const r = await commit({
        token: staged.token,
        options,
        title: title.trim(),
        description: description.trim(),
        grain_text: grain.trim() || null,
        date_column: dateColumn || null,
        examples,
        replace_id: replace?.id ?? null
      });
      onDone(r.topic);
    } catch (e) {
      fail(e);
      setBusy(null);
    }
  };

  const dateColumns = preview?.columns.filter((c) => c.type === 'date') ?? [];

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-4 py-6 sm:px-6">
      <button
        type="button"
        onClick={onCancel}
        className="flex w-fit items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" aria-hidden="true" />
        Back to Your data
      </button>

      <Steps step={step} />

      {error && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/30 dark:text-red-200"
        >
          <AlertTriangle
            className="mt-0.5 size-4 shrink-0"
            aria-hidden="true"
          />
          <span>{error}</span>
        </div>
      )}

      {/* ── 1 Source ─────────────────────────────────────────────────── */}
      {step === 1 && (
        <div className="flex flex-col gap-6">
          <div className="space-y-1">
            <h1 className="text-2xl font-semibold">
              {replace
                ? `New version of ${replace.title}`
                : 'Where is the data?'}
            </h1>
            <p className="text-sm text-muted-foreground">
              Bring the sheet as it is. There is no template to fill and no
              columns to match.
            </p>
          </div>

          <section className="flex flex-col gap-3 rounded-2xl border border-border p-5">
            <h2 className="text-sm font-semibold">Upload a file</h2>
            <label
              htmlFor="data-file"
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragging(false);
                void onFile(e.dataTransfer.files?.[0]);
              }}
              className={
                'flex min-h-44 cursor-pointer flex-col items-center justify-center gap-1.5 rounded-xl border-2 border-dashed p-6 text-center transition-colors ' +
                (dragging
                  ? 'border-primary bg-primary/10'
                  : 'border-primary/60 bg-primary/5 hover:bg-primary/10')
              }
            >
              {busy ? (
                <Loader2
                  className="size-7 animate-spin text-primary"
                  aria-hidden="true"
                />
              ) : (
                <Upload className="size-7 text-primary" aria-hidden="true" />
              )}
              <span className="text-base font-semibold">
                {busy ?? 'Drop your Excel or CSV here'}
              </span>
              {!busy && (
                <span className="text-sm text-muted-foreground">
                  or click to browse
                </span>
              )}
              <span className="mt-1 text-xs text-muted-foreground">
                .xlsx · .csv · up to 20 MB or 200,000 rows · one sheet per topic
              </span>
            </label>
            <input
              id="data-file"
              ref={fileInput}
              type="file"
              accept={ACCEPT}
              className="sr-only"
              disabled={!!busy}
              onChange={(e) => {
                void onFile(e.target.files?.[0]);
                e.target.value = '';
              }}
            />
          </section>

          <div className="flex items-center gap-3">
            <span aria-hidden="true" className="h-px flex-1 bg-border" />
            <span className="text-xs text-muted-foreground">or</span>
            <span aria-hidden="true" className="h-px flex-1 bg-border" />
          </div>

          <section className="flex flex-col gap-3 rounded-2xl border border-border p-5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-sm font-semibold">Link a Google Sheet</h2>
              <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-200">
                Refresh any time
              </span>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="sheet-url">Sheet link</Label>
              <div className="flex flex-wrap gap-2">
                <Input
                  id="sheet-url"
                  type="url"
                  inputMode="url"
                  placeholder="https://docs.google.com/spreadsheets/d/…"
                  value={sheetUrl}
                  disabled={!!busy}
                  onChange={(e) => setSheetUrl(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void onLink();
                  }}
                  className="min-w-0 flex-1 basis-80"
                />
                <Button
                  type="button"
                  variant="outline"
                  disabled={!!busy || !sheetUrl.trim()}
                  onClick={() => void onLink()}
                >
                  <Link2 aria-hidden="true" />
                  Connect
                </Button>
              </div>
            </div>
            <ul className="list-disc space-y-0.5 pl-5 text-sm text-muted-foreground">
              <li>
                Set the sheet to <strong>Anyone with the link can view</strong>.
                Signals never writes to it.
              </li>
              <li>
                Signals reads it now, once. Changed the sheet? Press{' '}
                <strong>Refresh</strong> on its card: it takes seconds.
              </li>
              <li>
                No Google sign-in and no nightly job. The link is all Signals
                keeps.
              </li>
            </ul>
          </section>

          <section className="grid gap-3 sm:grid-cols-3">
            {[
              [
                'No template',
                'Your column names stay as they are, Arabic included. Signals learns them.'
              ],
              [
                'Works next to your ERP',
                'One question can use your sales from the ERP and the targets from this sheet.'
              ],
              [
                'Only your company',
                "Stored in your company's own database. Everyone in the company can ask about it."
              ]
            ].map(([h, t]) => (
              <div
                key={h}
                className="flex flex-col gap-1 rounded-xl bg-muted p-4"
              >
                <span className="text-sm font-semibold">{h}</span>
                <span className="text-xs leading-5 text-muted-foreground">
                  {t}
                </span>
              </div>
            ))}
          </section>
        </div>
      )}

      {/* ── 2 Check ──────────────────────────────────────────────────── */}
      {step === 2 && preview && (
        <div className="flex flex-col gap-5">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="space-y-1">
              <h1 className="text-2xl font-semibold">Does this look right?</h1>
              <p className="text-sm text-muted-foreground">
                {staged?.file_name ?? 'Google Sheet'} · header on row{' '}
                {preview.header_row} ·{' '}
                <strong className="text-foreground">
                  {formatCount(preview.row_count)} rows · {preview.column_count}{' '}
                  columns
                </strong>
              </p>
            </div>
            {preview.sheets.length > 1 && (
              <div
                role="tablist"
                aria-label="Sheets"
                className="flex gap-0.5 rounded-lg bg-muted p-0.5"
              >
                {preview.sheets.map((s) => (
                  <button
                    key={s}
                    type="button"
                    role="tab"
                    aria-selected={s === preview.sheet}
                    disabled={!!busy}
                    onClick={() =>
                      s !== preview.sheet &&
                      void apply({
                        ...options,
                        sheet: s,
                        header_row: null,
                        text_columns: []
                      })
                    }
                    className={
                      'rounded-md px-3 py-1.5 text-xs font-medium ' +
                      (s === preview.sheet
                        ? 'bg-background font-semibold shadow-sm'
                        : 'text-muted-foreground hover:text-foreground')
                    }
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-start gap-5">
            <section
              aria-label="Preview"
              className="min-w-0 flex-[999_1_480px] overflow-hidden rounded-2xl border border-border"
            >
              <div className="overflow-x-auto">
                <table className="w-full min-w-[560px] border-collapse text-sm">
                  <thead>
                    <tr className="bg-muted/60">
                      {preview.columns.map((c) => (
                        <th
                          key={c.name}
                          scope="col"
                          className={
                            'border-b border-border px-3 py-2 align-top font-semibold ' +
                            (c.type === 'number' ? 'text-right' : 'text-left')
                          }
                        >
                          <div
                            className={
                              'flex flex-col gap-1 ' +
                              (c.type === 'number'
                                ? 'items-end'
                                : 'items-start')
                            }
                          >
                            <span>{c.name}</span>
                            <TypeChip column={c} />
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.sample_rows.map((row, i) => (
                      <tr key={i}>
                        {preview.columns.map((c) => (
                          <td
                            key={c.name}
                            className={
                              'border-b border-border/60 px-3 py-1.5 ' +
                              (c.type === 'number'
                                ? 'text-right tabular-nums'
                                : 'text-left') +
                              (row[c.name] == null
                                ? ' text-muted-foreground'
                                : '')
                            }
                          >
                            {cell(row[c.name])}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border bg-muted/60 px-3 py-2 text-xs text-muted-foreground">
                <span>
                  Showing{' '}
                  {Math.min(preview.sample_rows.length, preview.row_count)} of{' '}
                  {formatCount(preview.row_count)} rows
                </span>
                <span>Columns keep their original names. Nothing to map.</span>
              </div>
            </section>

            <aside className="flex min-w-0 flex-[1_1_280px] flex-col gap-3">
              <h2 className="text-sm font-semibold">Things we noticed</h2>
              {preview.notices.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  Nothing to say: the sheet was read exactly as it is.
                </p>
              )}
              {preview.notices.map((n) => (
                <NoticeCard
                  key={n.id}
                  notice={n}
                  busy={!!busy}
                  onUndo={(undo) => void apply(applyUndo(options, undo))}
                />
              ))}
              <div className="flex flex-col gap-1.5 rounded-xl border border-border p-3.5">
                <Label htmlFor="header-row" className="text-sm font-semibold">
                  Header is on row
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="header-row"
                    type="number"
                    min={1}
                    value={headerRow}
                    disabled={!!busy}
                    onChange={(e) => setHeaderRow(e.target.value)}
                    className="w-24"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={
                      !!busy || Number(headerRow) === preview.header_row
                    }
                    onClick={() =>
                      void apply({
                        ...options,
                        header_row: Number(headerRow) || null
                      })
                    }
                  >
                    Read from there
                  </Button>
                </div>
              </div>
              {(options.keep_totals ||
                (options.text_columns?.length ?? 0) > 0 ||
                options.two_row_header === false ||
                options.header_row != null) && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="w-fit"
                  disabled={!!busy}
                  onClick={() => void apply({ sheet: options.sheet })}
                >
                  <RotateCcw aria-hidden="true" />
                  Back to what Signals read first
                </Button>
              )}
            </aside>
          </div>

          <div className="flex flex-wrap justify-between gap-2 pt-1">
            <Button
              type="button"
              variant="outline"
              disabled={!!busy}
              onClick={() => setStep(1)}
            >
              Back
            </Button>
            <Button
              type="button"
              disabled={!!busy}
              onClick={() => {
                setStep(3);
                if (!draft) void draftNow(replace?.title ?? null);
              }}
            >
              {busy ? (
                <Loader2 className="animate-spin" aria-hidden="true" />
              ) : null}
              Looks right, continue
            </Button>
          </div>
        </div>
      )}

      {/* ── 3 Describe ───────────────────────────────────────────────── */}
      {step === 3 && preview && (
        <div className="flex flex-col gap-5">
          <div className="space-y-1">
            <h1 className="text-2xl font-semibold">
              Tell Signals what this is
            </h1>
            <p className="text-sm text-muted-foreground">
              {replace
                ? 'The name and the words stay as they were; change them if the sheet changed.'
                : 'We drafted it from your columns. Fix anything that is wrong: this is what Signals reads before every answer.'}
            </p>
          </div>

          <section className="flex flex-col gap-5 rounded-2xl border border-border p-5">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="topic-title">Name</Label>
              <Input
                id="topic-title"
                value={title}
                maxLength={120}
                onChange={(e) => setTitle(e.target.value)}
                className="max-w-md"
              />
              <span className="text-xs text-muted-foreground">
                How it appears in "What can I ask?" and in answers.
              </span>
            </div>

            <div className="flex flex-col gap-1.5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Label htmlFor="topic-desc">What is this data?</Label>
                <div className="flex items-center gap-2">
                  {draft && (
                    <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-200">
                      {draft.source === 'model'
                        ? 'Drafted by Signals'
                        : 'Drafted from the columns'}
                    </span>
                  )}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={!!busy}
                    onClick={() => void draftNow(title.trim() || null)}
                  >
                    <Sparkles aria-hidden="true" />
                    Draft again
                  </Button>
                </div>
              </div>
              <Textarea
                id="topic-desc"
                rows={5}
                maxLength={2000}
                value={busy === 'Drafting…' && !description ? '' : description}
                placeholder={busy === 'Drafting…' ? 'Drafting…' : ''}
                onChange={(e) => setDescription(e.target.value)}
              />
              <span className="text-xs text-muted-foreground">
                Plain words are best. Say what a row is, what the numbers mean,
                and anything the sheet cannot answer.
              </span>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="topic-grain">One row is</Label>
                <Input
                  id="topic-grain"
                  value={grain}
                  maxLength={160}
                  placeholder={
                    preview.grain?.text ?? 'one customer, one invoice…'
                  }
                  onChange={(e) => setGrain(e.target.value)}
                />
                {preview.grain && (
                  <span className="text-xs text-muted-foreground">
                    Guessed from {preview.grain.columns.join(' + ')} being
                    unique.
                  </span>
                )}
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="topic-date">
                  Date column{' '}
                  <span className="font-normal text-muted-foreground">
                    (optional)
                  </span>
                </Label>
                <select
                  id="topic-date"
                  value={dateColumn}
                  onChange={(e) => setDateColumn(e.target.value)}
                  className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="">No date in this data</option>
                  {dateColumns.map((c) => (
                    <option key={c.name} value={c.name}>
                      {c.name}
                    </option>
                  ))}
                </select>
                <span className="text-xs text-muted-foreground">
                  Lets you ask "this quarter" or "vs last year".
                </span>
              </div>
            </div>
          </section>

          <section className="grid gap-6 rounded-2xl border border-border p-5 sm:grid-cols-2">
            <fieldset className="flex flex-col gap-3">
              <legend className="mb-1 text-sm font-semibold">
                Who can see it
              </legend>
              <RadioGroup value="everyone">
                <label className="flex items-start gap-2.5 text-sm">
                  <RadioGroupItem value="everyone" className="mt-0.5" />
                  <span>
                    <span className="font-medium">Everyone in the company</span>
                    <br />
                    <span className="text-xs text-muted-foreground">
                      Same as your ERP topics. Admins can limit it in
                      Configurations.
                    </span>
                  </span>
                </label>
                <label className="flex items-start gap-2.5 text-sm opacity-60">
                  <RadioGroupItem value="me" disabled className="mt-0.5" />
                  <span>
                    <span className="font-medium">Only me</span>{' '}
                    <span className="text-xs text-muted-foreground">
                      Coming soon
                    </span>
                  </span>
                </label>
                <label className="flex items-start gap-2.5 text-sm opacity-60">
                  <RadioGroupItem value="people" disabled className="mt-0.5" />
                  <span>
                    <span className="font-medium">Choose people</span>{' '}
                    <span className="text-xs text-muted-foreground">
                      Coming soon
                    </span>
                  </span>
                </label>
              </RadioGroup>
            </fieldset>
            <fieldset className="flex flex-col gap-3">
              <legend className="mb-1 text-sm font-semibold">
                When a new version is uploaded
              </legend>
              <RadioGroup value="replace">
                <label className="flex items-start gap-2.5 text-sm">
                  <RadioGroupItem value="replace" className="mt-0.5" />
                  <span>
                    <span className="font-medium">Replace everything</span>
                    <br />
                    <span className="text-xs text-muted-foreground">
                      The new file is the whole truth. Simplest and safest.
                    </span>
                  </span>
                </label>
                <label className="flex items-start gap-2.5 text-sm opacity-60">
                  <RadioGroupItem value="append" disabled className="mt-0.5" />
                  <span>
                    <span className="font-medium">Add the new rows</span>{' '}
                    <span className="text-xs text-muted-foreground">
                      Coming soon
                    </span>
                  </span>
                </label>
                <label className="flex items-start gap-2.5 text-sm opacity-60">
                  <RadioGroupItem value="upsert" disabled className="mt-0.5" />
                  <span>
                    <span className="font-medium">Update rows that match</span>{' '}
                    <span className="text-xs text-muted-foreground">
                      Coming soon
                    </span>
                  </span>
                </label>
              </RadioGroup>
            </fieldset>
          </section>

          <p className="flex items-start gap-2 rounded-xl bg-muted px-4 py-3 text-sm text-muted-foreground">
            <FileSpreadsheet
              className="mt-0.5 size-4 shrink-0"
              aria-hidden="true"
            />
            <span>
              Ready in a moment. Signals adds it to What can I ask?, and your
              next question can use it together with your ERP data. Open chats
              pick it up on their next question.
            </span>
          </p>

          <div className="flex flex-wrap justify-between gap-2 pt-1">
            <Button
              type="button"
              variant="outline"
              disabled={!!busy}
              onClick={() => setStep(2)}
            >
              Back
            </Button>
            <Button type="button" disabled={!!busy} onClick={() => void save()}>
              {busy === 'Saving…' ? (
                <Loader2 className="animate-spin" aria-hidden="true" />
              ) : null}
              {replace ? 'Save the new version' : 'Save and ask Signals'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
